// /src/pages/private/members/Calendar.jsx

import React, { useCallback, lazy, Suspense, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../authContext';
import { useSecurity } from '../../../SecurityContext';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import LoadingSpinner from '../../../components/LoadingSpinner';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import CustomToolbar from './CalendarCustomToolbar';
import useCalendarEvents from './calendarTools/calendar/hooks/useCalendarEvents';
import useDateCellClasses from './calendarTools/calendar/hooks/useDateCellClasses';
import useEventStatusClock from './calendarTools/calendar/hooks/useEventStatusClock';
import { getEventStatus } from './calendarTools/calendar/utils/calendarEventUtils';
import GapAwareMonthView from './calendarTools/monthViewGap/GapAwareMonthView';
import { CalendarMentionNavContext } from './calendarMentionNavContext';
import {
  EVENT_AUCTION_ACCESS_RESOURCE,
  EVENT_AUCTION_MANAGE_RESOURCE,
  EVENT_AUCTION_ROUTE,
} from './calendarTools/event/auctions/eventAuctionConstants';
import CalendarSignupIntroPopup from './calendarTools/CalendarSignupIntroPopup';

moment.updateLocale('en', { week: { dow: 1 } }); // dow: 1 sets Monday as the first day of the week
const localizer = momentLocalizer(moment);

// Lazy load components
const CalendarCreateEvent = lazy(() =>
  import(/* webpackChunkName: "calendar-create-event" */ "./calendarTools/CalendarCreateEvent")
);
const CalendarEvent = lazy(() =>
  import(/* webpackChunkName: "calendar-event" */ "./calendarTools/CalendarEvent")
);
const CalendarEventReport = lazy(() =>
  import(/* webpackChunkName: "calendar-event-report" */ "./calendarTools/CalendarEventReport")
);

function Calendar() {
  const { t } = useTranslation();
  const { handles } = useAuth();
  const {
    subjectHasPermission,
    isViewAsActive,
    viewAsLoading,
    viewAsUserId,
  } = useSecurity();
  const viewAsSubjectReady = !isViewAsActive || !viewAsLoading;
  const [showCalendarEvent, setShowCalendarEvent] = useState(false);
  const [activeEventId, setActiveEventId] = useState(null);
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [openAuctionOnLoad, setOpenAuctionOnLoad] = useState(false);
  const location = useLocation();
  // Create-event moniker is viewer-only; mutations are blocked while view-as is active.
  const firstMoniker = !isViewAsActive && handles && handles.length > 0
    ? handles[0].moniker
    : null;
  const [showModal, setShowModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [currentView, setCurrentView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarBaseDate, setCalendarBaseDate] = useState(moment());

  const canCreateEvent =
    viewAsSubjectReady &&
    !isViewAsActive &&
    subjectHasPermission("private.navbar.calendar.create", "create");
  const canReadCalendar =
    viewAsSubjectReady &&
    subjectHasPermission("private.navbar.calendar", "read");

  const openCalendarEventModal = useCallback((event) => {
    setActiveEventId(event.id);
    setSelectedOccurrence({
      startTime: event.start,
      endTime: event.end,
      occurrenceId: event.occurrenceId
    });
    setShowCalendarEvent(true);
  }, []);

  const onOpenAuctionFromRoute = useCallback(() => {
    setOpenAuctionOnLoad(true);
  }, []);

  const { events, timezone, currentPeriodRef, fetchEventsForPeriod, handleEventUpdated } = useCalendarEvents({
    userHasPermission: subjectHasPermission,
    location,
    openCalendarEventModal,
    onOpenAuctionFromRoute,
    viewAsSubjectReady,
    viewAsUserId,
    isViewAsActive,
  });

  useEventStatusClock(canReadCalendar);

  useDateCellClasses({ currentView, calendarBaseDate, events });

  const openReportModal = (occurrenceId) => {
    setShowCalendarEvent(false); // Fermer CalendarEvent
    setSelectedOccurrence({ occurrenceId }); // Stocker l'occurrence pour le modal de rapport
    setShowReportModal(true); // Ouvrir CalendarEventReport
  };

  const closeReportModal = () => {
    setShowReportModal(false);
  };

  // Fonction pour fermer le modal CalendarEvent
  const closeCalendarEventModal = () => {
    setShowCalendarEvent(false);
    setOpenAuctionOnLoad(false);
  };

  const handleSelectSlot = ({ start, end }) => {
    if (canCreateEvent) {
      if (currentView === 'month') {
        setSelectedDate(start);
        setSelectedRange(null);
      } else {
        setSelectedRange({ start, end });
        setSelectedDate(null);
      }
      setShowModal(true);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    let start, end;
    if (view === 'month') {
      start = moment().startOf('month').toISOString();
      end = moment().endOf('month').toISOString();
    } else if (view === 'week') {
      start = moment().startOf('week').toISOString();
      end = moment().endOf('week').toISOString();
    } else if (view === 'day') {
      start = moment().startOf('day').toISOString();
      end = moment().endOf('day').toISOString();
    } else if (view === 'agenda') {
      start = moment().startOf('day').toISOString();
      end = moment().add(1, 'month').endOf('day').toISOString();
    }

    if (start && end) {
      currentPeriodRef.current = { start, end };
      fetchEventsForPeriod(start, end);
    } else {
      console.error("Invalid view change with view:", view);
    }
  };

  const calendarMentionNavValue = useMemo(() => {
    const raw = selectedOccurrence?.occurrenceId;
    if (raw == null || raw === "") return { calendarOccurrenceId: null };
    const n = Number(raw);
    return {
      calendarOccurrenceId: Number.isFinite(n) && n > 0 ? n : null
    };
  }, [selectedOccurrence?.occurrenceId]);

  if (!viewAsSubjectReady) {
    return (
      <div data-resource="private.navbar.calendar" data-action="read" data-description="Navbar → Calendar">
        <div className="margin-medium-spacer" />
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <CalendarMentionNavContext.Provider value={calendarMentionNavValue}>
    <div data-resource="private.navbar.calendar" data-action="read" data-description="Navbar → Calendar">
            <CalendarSignupIntroPopup
              enabled={canReadCalendar}
              suppress={showCalendarEvent || showReportModal}
              subjectUserId={
                isViewAsActive && viewAsUserId != null ? Number(viewAsUserId) : null
              }
            />
            <div className='margin-medium-spacer'></div>
            <div className="table-base roster calendar-page-table-shell">
            <div
            data-resource="private.navbar.calendar.create"
            data-action="create"
            data-route="/api/admin/create-event" data-description="Navbar → Calendar → Create">
        
                <BigCalendar
          localizer={localizer}
          views={{
            month: GapAwareMonthView,
            week: true,
            day: true,
            agenda: true
          }}
          events={events}
          startAccessor="start"
          endAccessor="end"
          selectable={canCreateEvent}
          onSelectEvent={(event) => {
            setSelectedEvent(event);
            openCalendarEventModal(event);
          }}
          onSelectSlot={handleSelectSlot}
          selected={selectedEvent}
          components={{
            header: ({ label }) => (
              <div className="header-content">
                <span className="header-label" role="columnheader" aria-sort="none">
                  {label}
                </span>
              </div>
            ),
            toolbar: (props) =>
            <CustomToolbar
              {...props}
              onEventCreated={fetchEventsForPeriod} />,


            event: ({ event }) =>
            <div>
                                {currentView === 'month' ?
              <div>
                                        <div>{moment(event.start).format('HH:mm')} - {event.title}</div>
                                    </div> :

              <div>{event.title}</div>
              }
                            </div>

          }}
          eventPropGetter={(event) => {
            let newClassName = 'rbc-event';
            const status = getEventStatus(event); // Récupère le statut

            // Add a class based on event status
            if (status === 'Canceled') {
              newClassName += ' canceled';
            } else if (status === 'Tentative') {
              newClassName += ' tentative';
            } else if (status === 'Scheduled') {
              newClassName += ' scheduled';
            } else if (status === 'Live') {
              newClassName += ' live';
            } else if (status === 'Concluded') {
              newClassName += ' concluded';
            }

            return {
              className: newClassName
            };
          }}
          formats={{
            weekdayFormat: (date, culture, localizer) =>
            localizer.format(date, 'dddd', culture) // Full day name
          }}
          onView={handleViewChange}
          onRangeChange={(range) => {
            if (range.start && range.end) {
              currentPeriodRef.current = {
                start: range.start.toISOString(),
                end: range.end.toISOString()
              };
              fetchEventsForPeriod(currentPeriodRef.current.start, currentPeriodRef.current.end);
            } else {
              console.error("Range change received an invalid range:", range);
            }
          }}

          onNavigate={(date, view, action) => {
            setCurrentView(view);
            setCalendarBaseDate(moment(date)); // 👈 c'est ici qu'on corrige la base

            const start = moment(date).startOf(view).toISOString();
            const end = moment(date).endOf(view).toISOString();

            currentPeriodRef.current = { start, end };
            fetchEventsForPeriod(start, end);
          }}

          view={currentView}
          onDrillDown={(date, view) => {
            handleViewChange(view);
          }} />
        
            </div>

            <div className="roster-table-footer calendar-page-footer">
              <div className="timezone-display">
                <p>
                  Your timezone is: {timezone}
                </p>
              </div>
            </div>
            </div>
            
            <div
        className="display-none"
        data-resource="private.navbar.calendar"
        data-action="read"
        data-route="/api/user/has-event-signup"
        data-description="Navbar → Calendar (first signup intro)" />
            <div
        className="display-none"
        data-resource="private.navbar.calendar"
        data-action="read"
        data-route="/api/user/calendar-diff" data-description="Navbar → Calendar (WS sync)" />
            <div
        className="display-none"
        data-resource="private.navbar.calendar"
        data-action="read"
        data-route="/api/user/calendar-events-period" data-description="Navbar → Calendar (period data)" />
            <div
        className="display-none"
        data-resource="private.navbar.calendar.public"
        data-action="read"
        data-description="Navbar → Calendar → Public events" />
            <div
        className="display-none"
        data-resource="private.navbar.calendar.private"
        data-action="read"
        data-description="Navbar → Calendar → Private events" />
      
            <div
        className="display-none"
        data-resource="private.navbar.calendar"
        data-action="read"
        data-route="/api/admin/retrieve-event-by-id" data-description="Navbar → Calendar" />

            <div
              className="display-none"
              data-resource={EVENT_AUCTION_MANAGE_RESOURCE}
              data-action="read"
              data-route={EVENT_AUCTION_ROUTE}
              data-description="Navbar → Calendar → Event auctions (manage)"
            />
            <div
              className="display-none"
              data-resource={EVENT_AUCTION_MANAGE_RESOURCE}
              data-action="write"
              data-description="Navbar → Calendar → Event auctions (manage write)"
            />
            <div
              className="display-none"
              data-resource={EVENT_AUCTION_ACCESS_RESOURCE}
              data-action="read"
              data-route={EVENT_AUCTION_ROUTE}
              data-description="Navbar → Calendar → Event auctions (signup access)"
            />
      
            <Suspense fallback={<LoadingSpinner />}>
                <CalendarCreateEvent
          show={showModal}
          onClose={() => {
            setShowModal(false);
            const { start, end } = currentPeriodRef.current;
            if (start && end) {
              fetchEventsForPeriod(start, end);
            }
          }}
          onEventCreated={() => {
            // Fermer le modal et rafraîchir les événements
            setShowModal(false);
            const { start, end } = currentPeriodRef.current;
            if (start && end) {
              fetchEventsForPeriod(start, end);
            }
          }}
          selectedDate={selectedDate || selectedRange}
          firstMoniker={firstMoniker} />
        
            </Suspense>

            <Suspense fallback={<LoadingSpinner />}>
                {showCalendarEvent && activeEventId &&
        <CalendarEvent
          eventId={activeEventId}
          show={showCalendarEvent}
          onClose={closeCalendarEventModal}
          selectedOccurrence={selectedOccurrence}
          onReport={openReportModal}
          onEventUpdated={handleEventUpdated}
          initialShowAuction={openAuctionOnLoad}
          onAuctionClosed={() => setOpenAuctionOnLoad(false)}
        />

        }
             </Suspense>

             <Suspense fallback={<LoadingSpinner />}>
                {/* Afficher CalendarEventReport si showReportModal est vrai */}
                {showReportModal && selectedOccurrence &&
        <CalendarEventReport
          eventOccurrenceId={selectedOccurrence?.occurrenceId}
          onClose={closeReportModal} />

        }
            </Suspense>
        </div>
    </CalendarMentionNavContext.Provider>);

}

export default Calendar;